import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { Users, Briefcase, Check, ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import QuoteForm from "@/components/QuoteForm";
import api from "@/lib/api";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [vehicle, setVehicle] = useState(null);

  useEffect(() => {
    api.get(`/vehicles/${id}`).then((res) => setVehicle(res.data)).catch(() => navigate("/fleet"));
  }, [id, navigate]);

  useEffect(() => {
    if (vehicle && location.hash === "#request-quote") {
      let tries = 0;
      const attempt = () => {
        const el = document.getElementById("request-quote");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (tries < 20) {
          tries += 1;
          setTimeout(attempt, 100);
        }
      };
      attempt();
    }
  }, [vehicle, location.hash]);

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="font-mono text-sm tracking-widest gold-text animate-pulse">LOADING…</span>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen" data-testid="vehicle-detail-page">
      <Navbar />

      <div className="pt-28 max-w-7xl mx-auto px-6">
        <Link to="/fleet" data-testid="back-to-fleet" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm">
          <ArrowLeft size={16} /> Back to Fleet
        </Link>
      </div>

      <section className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-12 gap-12">
        {/* LEFT: vehicle */}
        <div className="lg:col-span-7">
          <div className="suv-stage rounded-sm overflow-hidden border border-white/10">
            <img src={vehicle.image_url} alt={vehicle.name} className="suv-image w-full h-auto object-contain" data-testid="vehicle-image" />
          </div>
          <p className="text-xs tracking-label uppercase gold-text mt-8 mb-3">{vehicle.category}</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight" data-testid="vehicle-name">{vehicle.name}</h1>
          <p className="text-zinc-400 font-light mt-5 leading-relaxed max-w-2xl">{vehicle.description}</p>

          <div className="flex items-center gap-8 mt-8 text-zinc-300">
            <span className="flex items-center gap-2"><Users size={18} strokeWidth={1.5} className="gold-text" /> {vehicle.capacity} Passengers</span>
            <span className="flex items-center gap-2"><Briefcase size={18} strokeWidth={1.5} className="gold-text" /> {vehicle.luggage} Bags</span>
          </div>

          <div className="mt-10">
            <h3 className="text-xs tracking-label uppercase text-zinc-500 mb-5">Amenities</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {vehicle.features.map((f) => (
                <div key={f} className="flex items-center gap-3 text-zinc-300 font-light">
                  <Check size={16} className="gold-text shrink-0" /> {f}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: booking form */}
        <div className="lg:col-span-5">
          <div id="request-quote" className="scroll-mt-24 lg:sticky lg:top-28">
            <QuoteForm vehicle={vehicle} />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
